import React, { useState, useEffect } from 'react'
import { ChevronRight, ChevronDown, Folder, FolderOpen, Edit, Trash2 } from 'lucide-react'
import { Account } from '@/api/types'
import { accountsService } from '@/services/accountsService'
import { Button } from '@/components/ui/button'

interface AccountTreeProps {
  accounts: Account[]
  branchId: number
  onAccountSelect: (account: Account) => void
  onAddChild: (parentAccount: Account) => void
  onEdit: (account: Account) => void
  onDelete: (account: Account) => void
  selectedAccountId?: number
}

interface TreeNode extends Account {
  children?: TreeNode[]
  expanded?: boolean
  loading?: boolean
}

export const AccountTree: React.FC<AccountTreeProps> = ({
  accounts,
  branchId,
  onAccountSelect,
  onAddChild,
  onEdit,
  onDelete,
  selectedAccountId,
}) => {
  console.log('🟢 AccountTree rendering with accounts:', accounts.length, accounts)
  const [treeData, setTreeData] = useState<TreeNode[]>([])
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set())

  // Build tree structure from flat list
  useEffect(() => {
    const buildTree = (accounts: Account[]): TreeNode[] => {
      console.log('Building tree from accounts:', accounts.length, accounts)
      
      if (accounts.length === 0) {
        console.log('No accounts to build tree')
        return []
      }

      const accountMap = new Map<number, TreeNode>()
      const rootNodes: TreeNode[] = []

      // First pass: create all nodes
      accounts.forEach((account) => {
        const accountId = account.accountId || account.id || 0
        if (accountId === 0) {
          console.warn('Account with ID 0 found:', account)
          return
        }
        
        const node: TreeNode = {
          ...account,
          children: [],
          expanded: expandedNodes.has(accountId),
        }
        accountMap.set(accountId, node)
      })

      console.log('Account map created with', accountMap.size, 'nodes')

      // Second pass: build tree structure
      accounts.forEach((account) => {
        const accountId = account.accountId || account.id || 0
        if (accountId === 0) return
        
        const node = accountMap.get(accountId)
        if (!node) {
          console.warn('Node not found for accountId:', accountId)
          return
        }
        
        const parentId = account.parentAccountId || account.parentId || null

        // Root node: parentId is null, 0, or parent doesn't exist in current data
        // Only add as root if parentId is actually 0 or null (true root nodes)
        if (parentId === null || parentId === 0) {
          rootNodes.push(node)
          console.log('🟢 Root node added:', account.nameEn || account.name, 'ID:', accountId, 'parentId:', parentId)
        } else if (!accountMap.has(parentId)) {
          // Parent not in current data, treat as root
          rootNodes.push(node)
          console.log('🟢 Root node (parent not in data):', account.nameEn || account.name, 'ID:', accountId, 'parentId:', parentId)
        } else {
          // Has parent in data, add as child
          const parent = accountMap.get(parentId)
          if (parent) {
            if (!parent.children) {
              parent.children = []
            }
            parent.children.push(node)
            console.log('🟢 Child node added:', account.nameEn || account.name, 'to parent:', parent.nameEn || parent.name)
          }
        }
      })
      
      console.log('🟢 Root nodes before sorting:', rootNodes.length, rootNodes.map(n => ({ name: n.nameEn || n.name, code: n.code || n.accountCode })))

      // Sort root nodes by specific order: Liabilities, Assets, Equity, Revenue, Expenses
      // This ensures consistent ordering across all branches
      const accountOrder: { [key: string]: number } = {
        'liabilities': 1,
        'assets': 2,
        'equity': 3,
        'revenue': 4,
        'expenses': 5,
      }

      const getAccountOrder = (account: Account): number => {
        const name = (account.nameEn || account.name || account.nameAr || '').toLowerCase().trim()
        
        // Check for exact matches first
        for (const [key, order] of Object.entries(accountOrder)) {
          if (name === key) {
            return order
          }
        }
        
        // Check for partial matches (e.g., "Current Assets" contains "assets")
        for (const [key, order] of Object.entries(accountOrder)) {
          if (name.includes(key)) {
            return order
          }
        }
        
        // If no match, try to extract from code
        const code = account.code || account.accountCode || ''
        const numFromCode = code ? (parseInt(code) || parseInt(code.match(/\d+/)?.[0] || '0') || 0) : 0
        
        // Map code numbers to order (1=Liabilities, 2=Assets, 3=Equity, 4=Revenue, 7=Expenses)
        if (numFromCode === 1) return 1 // Liabilities
        if (numFromCode === 2) return 2 // Assets
        if (numFromCode === 3) return 3 // Equity
        if (numFromCode === 4) return 4 // Revenue
        if (numFromCode === 7) return 5 // Expenses
        
        // Default: put unmatched accounts at the end
        return 999
      }

      const sortByCode = (nodes: TreeNode[]): TreeNode[] => {
        return nodes.sort((a, b) => {
          const orderA = getAccountOrder(a)
          const orderB = getAccountOrder(b)
          
          // If same order, sort by code number
          if (orderA === orderB) {
            const codeA = a.code || a.accountCode || ''
            const codeB = b.code || b.accountCode || ''
            const numA = codeA ? (parseInt(codeA) || parseInt(codeA.match(/\d+/)?.[0] || '0') || 0) : 0
            const numB = codeB ? (parseInt(codeB) || parseInt(codeB.match(/\d+/)?.[0] || '0') || 0) : 0
            return numA - numB
          }
          
          return orderA - orderB
        }).map(node => {
          // Recursively sort children
          if (node.children && node.children.length > 0) {
            node.children = sortByCode(node.children)
          }
          return node
        })
      }

      const sortedRootNodes = sortByCode(rootNodes)
      console.log('🟢 Tree built with', sortedRootNodes.length, 'root nodes (sorted)')
      console.log('🟢 Root nodes details:', sortedRootNodes.map(n => ({
        name: n.nameEn || n.name || n.nameAr,
        code: n.code || n.accountCode,
        id: n.accountId || n.id,
        parentId: n.parentAccountId || n.parentId
      })))
      return sortedRootNodes
    }

    setTreeData(buildTree(accounts))
  }, [accounts, expandedNodes])

  const toggleNode = async (node: TreeNode) => {
    const nodeId = node.accountId || node.id || 0
    
    if (expandedNodes.has(nodeId)) {
      setExpandedNodes((prev) => {
        const newSet = new Set(prev)
        newSet.delete(nodeId)
        return newSet
      })
    } else {
      setExpandedNodes((prev) => new Set(prev).add(nodeId))
      
      // Load children if not already loaded
      if (!node.children || node.children.length === 0) {
        try {
          const children = await accountsService.getAccountsByParentId(nodeId, branchId)
          
          // Sort children by code number
          const sortedChildren = children.sort((a, b) => {
            const codeA = a.code || a.accountCode || ''
            const codeB = b.code || b.accountCode || ''
            
            const numA = codeA ? (parseInt(codeA) || parseInt(codeA.match(/\d+/)?.[0] || '0') || 0) : 0
            const numB = codeB ? (parseInt(codeB) || parseInt(codeB.match(/\d+/)?.[0] || '0') || 0) : 0
            
            return numA - numB
          })
          
          // Update tree with sorted children
          setTreeData((prev) => {
            const updateNode = (nodes: TreeNode[]): TreeNode[] => {
              return nodes.map((n) => {
                if ((n.accountId || n.id) === nodeId) {
                  return {
                    ...n,
                    children: sortedChildren.map((child) => ({
                      ...child,
                      children: [],
                      expanded: false,
                    })),
                  }
                }
                if (n.children) {
                  return { ...n, children: updateNode(n.children) }
                }
                return n
              })
            }
            return updateNode(prev)
          })
        } catch (error) {
          console.error('Failed to load children:', error)
        }
      }
    }
  }

  const renderNode = (node: TreeNode, level: number = 0): React.ReactNode => {
    const nodeId = node.accountId || node.id || 0
    const isExpanded = expandedNodes.has(nodeId)
    const hasChildren = node.children && node.children.length > 0
    const isSelected = selectedAccountId === nodeId
    const displayName = node.nameEn || node.name || node.nameAr || 'Unnamed'
    const displayCode = node.code || ''

    return (
      <div key={nodeId}>
        <div
          className={`flex items-center gap-2 p-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 ${
            isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500' : ''
          }`}
          style={{ paddingLeft: `${level * 24 + 8}px` }}
          onClick={() => onAccountSelect(node)}
        >
          {/* Expand/Collapse Icon */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              toggleNode(node)
            }}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
            disabled={!hasChildren}
          >
            {hasChildren ? (
              isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )
            ) : (
              <div className="w-4 h-4" />
            )}
          </button>

          {/* Folder Icon */}
          {isExpanded ? (
            <FolderOpen className="w-4 h-4 text-blue-500" />
          ) : (
            <Folder className="w-4 h-4 text-gray-500" />
          )}

          {/* Account Info */}
          <div className="flex-1 flex items-center gap-2 cursor-pointer">
            <span className="font-medium">{displayName}</span>
            {displayCode && (
              <span className="text-sm text-gray-500">- {displayCode}</span>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={(e) => {
                e.stopPropagation()
                onAddChild(node)
              }}
              title="Add Child"
            >
              <span className="text-xs">+</span>
            </Button>
            {(node.level || 0) === 5 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-blue-600 hover:text-blue-700"
                  onClick={(e) => {
                    e.stopPropagation()
                    onEdit(node)
                  }}
                  title="Edit"
                >
                  <Edit className="w-3 h-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-red-600 hover:text-red-700"
                  onClick={(e) => {
                    e.stopPropagation()
                    onDelete(node)
                  }}
                  title="Delete"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Render Children */}
        {isExpanded && hasChildren && node.children && (
          <div>
            {node.children.map((child) => renderNode(child, level + 1))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-1">
      {treeData.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No accounts found
        </div>
      ) : (
        treeData.map((node) => renderNode(node, 0))
      )}
    </div>
  )
}

